import { ABSENT, formatCents, formatCount, formatMonth, formatNumber } from "@/shared/format";

/**
 * MONEY, IN ONE PLACE — and that place is now `@/shared/format`.
 *
 * ── WHAT THIS FILE IS AFTER THE MOVE ──────────────────────────────────────
 *
 * The formatters moved out and are re-exported below, unchanged in name and in
 * output. They had to move because they hardcoded `"pt-BR"` and carried a
 * hand-written list of month names, and a panel that can be read in two
 * languages cannot have one feature's opinion about what a number looks like.
 *
 * What STAYED is the arithmetic of cents — `parseCents`, `centsToInput`,
 * `pricePerThousand`, `percentOf`, `meterTone`. That is not formatting: it is
 * what this feature knows about its own numbers, and it is the reason this file
 * is still the only place in billing that divides by a hundred. `.toFixed(2)`
 * scattered across twenty components is how a total on one screen stops matching
 * the same total on another, and it is exactly what V2 did with `numeric(10,2)`
 * read as a `Number`.
 *
 * Every formatter still answers ABSENCE explicitly. A missing number is drawn as
 * "—" and never as `R$ 0,00`: a zero is a fact and a blank is a different fact,
 * and a bill whose total nobody can account for is worse than no bill at all.
 */

export {
	ABSENT,
	formatBasisPoints,
	formatCents,
	formatCentsPlain,
	formatCount,
	formatDate,
	formatDateTime,
	formatDuration,
} from "@/shared/format";

/** Whole credits. Never a price, never a fraction. */
export function formatCredits(credits: number | null | undefined): string {
	return formatCount(credits);
}

/**
 * Quantas RESPOSTAS INTELIGENTES cabem num saldo de créditos.
 *
 * ── POR QUE ESTE NÚMERO EXISTE ────────────────────────────────────────────
 *
 * "8.000 créditos por mês" não é uma quantidade que alguém saiba julgar antes
 * de comprar: crédito é a unidade da casa, não uma unidade do mundo. A tela de
 * planos precisa de um número que a pessoa consiga comparar com o próprio dia,
 * e o único honesto é o de respostas — que é o que ela compra.
 *
 * ── POR QUE INTELIGENTE, E DE ONDE VEM O PREÇO ────────────────────────────
 *
 * Divide-se pelo preço do turno INTELIGENTE, o padrão do produto: dividir pelo
 * rápido daria uma promessa que quase ninguém cumpre, e pelo premium venderia o
 * plano por baixo. O preço NÃO está escrito aqui: vem de `GET /v3/credits` →
 * `prices.perAnswer` (`useAnswerPrice`) — o inteligente do `.env` quando o
 * atendimento cobra por nível, a resposta típica do workspace quando cobra
 * pelo custo (0280). Até 27/09/2026 era uma constante `3` — e a
 * frase ao lado dizia premium 16 enquanto a cobrança lia 8.
 *
 * Sem preço (servidor mais velho, pedido ainda a chegar, preço absurdo) a
 * resposta é `null` e a tela cala a linha: um número de respostas calculado
 * sobre um preço inventado é pior do que nenhum.
 *
 * PISO em vez de arredondamento: 2.500 créditos dão 833 respostas e não 834, e
 * um "≈" que arredonda para cima é o único tipo de aproximação que decepciona.
 */
export function smartAnswers(
	credits: number | null | undefined,
	perAnswer: number | null | undefined,
): number | null {
	if (perAnswer === null || perAnswer === undefined || !Number.isFinite(perAnswer)) return null;
	if (perAnswer <= 0) return null;
	if (credits === null || credits === undefined || !Number.isFinite(credits)) return 0;
	return Math.max(0, Math.floor(credits / perAnswer));
}

/** O mesmo número, escrito na língua de quem lê — `null` quando não há preço. */
export function formatSmartAnswers(
	credits: number | null | undefined,
	perAnswer: number | null | undefined,
): string | null {
	const n = smartAnswers(credits, perAnswer);
	return n === null ? null : formatCount(n);
}

/**
 * What the suppliers charged, in micro-USD.
 *
 * Shown in DOLLARS and labelled as such by `Intl`, which knows how each language
 * writes the symbol. Converting it to BRL here would be inventing a rate nobody
 * supplied, and this number is only ever read by the installation's own panel,
 * where the unit is not a surprise. Micro to cents is a division by ten
 * thousand — the currency's sub-unit is what the shared formatter speaks.
 */
export function formatMicroUsd(micro: number | null | undefined): string {
	if (micro === null || micro === undefined || !Number.isFinite(micro)) return ABSENT;
	return formatCents(Math.round(micro / 10_000), "USD");
}

/** `2026-03` written out. The month is a server fact; only the words are ours — and now `Intl`'s. */
export function formatReferenceMonth(month: string | null | undefined): string {
	return formatMonth(month);
}

/**
 * What somebody TYPED, as whole cents.
 *
 * Nobody types "29700" into a price box, so this turns "297", "297,00",
 * "1.234,56" and "1,234.56" into an integer — in the same file that owns the
 * way back, so the two cannot disagree.
 *
 * ── WHY THE LAST SEPARATOR DECIDES ────────────────────────────────────────
 *
 * This used to decide by the PRESENCE of a comma: comma anywhere meant pt-BR,
 * so the dot was stripped as a thousands mark. The day the panel learned
 * English that became a money bug — "1,234.56" lost its dot, became "1,23456",
 * and was stored as R$1.23. Nobody would have seen it until an invoice was
 * wrong.
 *
 * The rule that holds in both languages: whichever of `.` or `,` comes LAST is
 * the decimal mark, and the other one is grouping. That reads "1.234,56" and
 * "1,234.56" correctly without knowing who is typing.
 *
 * `locale` is the tie-breaker for the ONE genuinely ambiguous shape — a single
 * separator with exactly three digits after it, where "1,234" is a thousand in
 * English and one-point-two-three-four in Portuguese. Only there does the
 * reader's language change the answer.
 *
 * `null` means "not a number"; the caller decides whether that is an error or
 * an empty field.
 */
export function parseCents(text: string, locale = "pt-BR"): number | null {
	const trimmed = text.trim();
	if (!trimmed) return null;

	const lastDot = trimmed.lastIndexOf(".");
	const lastComma = trimmed.lastIndexOf(",");
	let decimal: "." | "," | null = null;

	if (lastDot >= 0 && lastComma >= 0) {
		decimal = lastDot > lastComma ? "." : ",";
	} else if (lastDot >= 0 || lastComma >= 0) {
		const mark = lastDot >= 0 ? "." : ",";
		const at = Math.max(lastDot, lastComma);
		const after = trimmed.length - at - 1;
		// Three digits after a lone separator is the ambiguous case, and the only
		// place the reader's language is allowed to change the number.
		const grouping = locale.toLowerCase().startsWith("pt") ? "." : ",";
		decimal = after === 3 ? (mark === grouping ? null : mark) : mark;
	}

	const normalized =
		decimal === null
			? trimmed.replace(/[.,]/g, "")
			: trimmed
					.split("")
					.filter((c, i) => (c !== "." && c !== "," ? true : i === Math.max(lastDot, lastComma)))
					.join("")
					.replace(decimal, ".");

	const value = Number(normalized);
	if (!Number.isFinite(value) || value < 0) return null;
	return Math.round(value * 100);
}

/**
 * The way back: whole cents into a text box, punctuated for the reader.
 *
 * The same locale that `parseCents` reads with, so a value typed, saved and
 * shown again survives the round trip in either language.
 */
export function centsToInput(cents: number | null | undefined, locale = "pt-BR"): string {
	if (cents === null || cents === undefined || !Number.isFinite(cents)) return "";
	const plain = (cents / 100).toFixed(2);
	return locale.toLowerCase().startsWith("pt") ? plain.replace(".", ",") : plain;
}

/**
 * Price per thousand credits — DERIVED, always, and never typed by anyone.
 *
 * It is the only number on the pack catalogue that compares two packs, and the
 * moment it is a column in a form it is a column that can disagree with the
 * price beside it.
 */
export function pricePerThousand(priceCents: number, credits: number, currency?: string): string {
	if (!credits) return ABSENT;
	return formatCents(Math.round((priceCents / credits) * 1000), currency);
}

/**
 * How full a meter is, capped at 100.
 *
 * A limit of zero is not "full": it is a resource that was never sold, and
 * dividing by it would draw a red bar over a workspace that owes nothing.
 *
 * ── UMA CASA DECIMAL, COM PISO ────────────────────────────────────────────
 *
 * `Math.round` stood here and it rounded 0,33% to `0`. On a card that says
 * "armazenamento" over 3,4 MB of real files, "0% usado" is not a rounding: it is
 * the screen saying nothing is stored while the file list says otherwise. One
 * decimal, and a floor of 0,1 so that anything ABOVE zero never reads as zero —
 * the only number allowed to draw "0%" is a true zero.
 */
export function percentOf(used: number, limit: number): number {
	if (!limit || limit <= 0) return 0;
	const raw = (used / limit) * 100;
	if (raw <= 0) return 0;
	return Math.min(100, Math.max(0.1, Math.round(raw * 10) / 10));
}

/**
 * The percentage as a person reads it, in their own language: "0,3" in pt-BR and
 * "0.3" in English. The `%` belongs to the phrase, not to the number, because
 * where the sign sits is a translation decision.
 */
export function formatPercentValue(percent: number): string {
	return formatNumber(percent, undefined, { maximumFractionDigits: 1 });
}

/** Green until 80%, amber to 100%, red at the wall. The same three everywhere. */
export function meterTone(percent: number): "success" | "warning" | "danger" {
	if (percent >= 100) return "danger";
	if (percent >= 80) return "warning";
	return "success";
}
